import { useTranslation } from 'react-i18next';

import { Text } from '../../../../../../components/atoms/text/text';
import { AnsweredStatus } from '../../../../utils/surveys/conditions';
import { WithIndicator } from '../../indicators/with-indicator';
import { ShowCalculation } from '../../show-calculation/show-calculation';
import { getThresholdScoreText } from '../get-threshold-score-text';
import { MeasureScoreSummaryProps } from '../types';

import { NotAdministeredText, NotAnswered } from './content-sections';

enum ContentType {
  NOT_ANSWERED = 'NOT_ANSWERED',
  NOT_ADMINISTERED = 'NOT_ADMINISTERED',
  SCORE = 'SCORE',
}

const getContentToRender = ({
  answeredStatus,
  showIndicators,
}: {
  answeredStatus: AnsweredStatus;
  showIndicators: boolean;
}): ContentType | null => {
  if (showIndicators) {
    if (answeredStatus === AnsweredStatus.NOT_SHOWN) return ContentType.NOT_ADMINISTERED;
    if (answeredStatus !== AnsweredStatus.COMPLETED) return ContentType.NOT_ANSWERED;
  }
  if (answeredStatus === AnsweredStatus.COMPLETED) return ContentType.SCORE;

  return null;
};

export const MeasureScoreSummary = ({
  answers,
  expression,
  isPdf = false,
  answeredStatus,
  respondentType,
  showIndicators,
  survey,
  thresholdByScore,
  value,
  hideMainMeasure,
}: MeasureScoreSummaryProps) => {
  const { t } = useTranslation();

  if (answeredStatus === AnsweredStatus.NOT_SHOWN && !showIndicators) return null;

  const contentType = getContentToRender({ answeredStatus, showIndicators });

  return (
    <WithIndicator displayIndicator={showIndicators} respondentType={respondentType}>
      {contentType === ContentType.NOT_ANSWERED && (
        <NotAnswered answeredStatus={answeredStatus} withIcon={!showIndicators} />
      )}
      {contentType === ContentType.NOT_ADMINISTERED && <NotAdministeredText />}
      {contentType === ContentType.SCORE && (
        <Text size="lg" weight="semibold">
          {!hideMainMeasure && (
            <>
              <span className="pr-1">
                {t('report.blocks.measureBreakdown.totalScore', {
                  value,
                  maxValue: expression.maxValue,
                })}
              </span>
              <span className="pr-1">| {getThresholdScoreText(thresholdByScore)}</span>
            </>
          )}
          <ShowCalculation
            inline
            isPdf={isPdf}
            measureName={expression.measureName}
            survey={survey}
            totalExpressionName={expression.name}
          />
        </Text>
      )}
    </WithIndicator>
  );
};
